/**
 * AircraftTab - Compact aircraft specs, dimensions, and compliance info
 *
 * Three blocks: Aircraft Specs, Compliance, Staff Notes.
 * Registration country with flag emoji, serial number, year manufactured.
 */

import { Ruler, Weight, MapPin, Shield, Wrench, Globe, Hash, CalendarDays, StickyNote } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AIRCRAFT_STATUS_LABELS, AIRCRAFT_STATUS_VARIANTS } from '@/types/aircrafts'
import { AIRCRAFT_CATEGORY_LABELS, type AircraftCategory } from '@/types/aircraft-types'
import {
  formatWeight,
  formatDate,
  getDaysUntil,
} from '@/lib/utils/format-business-data'
import { CompactInfoBlock } from './shared/CompactInfoBlock'
import { TooltipValue, CompactRow } from './shared/TooltipValue'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface AircraftTabProps {
  data: AllocationEnrichedData
}

/**
 * Country code → flag emoji (ISO 3166-1 alpha-2)
 */
function countryFlag(code: string | null): string | null {
  if (!code || code.length !== 2) return null
  const upper = code.toUpperCase()
  return String.fromCodePoint(
    ...Array.from(upper).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  )
}

/**
 * Format dimensions in compact format: "52.1L × 63.2W × 16.8H ft"
 */
function formatCompactDimensions(
  length: number | null,
  wingspan: number | null,
  height: number | null
): string | null {
  if (!length && !wingspan && !height) return null
  const parts = []
  if (length) parts.push(`${length}L`)
  if (wingspan) parts.push(`${wingspan}W`)
  if (height) parts.push(`${height}H`)
  return parts.join(' × ') + ' ft'
}

export function AircraftTab({ data }: AircraftTabProps) {
  const { aircraft, allocation } = data
  const openEditForm = useAircraftCardStore((s) => s.openEditForm)

  // Aircraft type display
  const typeName =
    aircraft.manufacturer || aircraft.model
      ? [aircraft.manufacturer, aircraft.model].filter(Boolean).join(' ')
      : aircraft.typeCode

  // Dimensions
  const dimensions = formatCompactDimensions(aircraft.length, aircraft.wingspan, aircraft.height)

  // Weight
  const weight = formatWeight(aircraft.maxTakeoffWeight)

  // Category badge
  const categoryLabel = aircraft.category
    ? AIRCRAFT_CATEGORY_LABELS[aircraft.category as AircraftCategory] || aircraft.category
    : null

  // Location (Area • Unit)
  const locationDisplay = [allocation.areaName, allocation.unitCode]
    .filter(Boolean)
    .join(' • ') || 'Not placed'

  // Registration country with flag
  const flag = countryFlag(aircraft.registrationCountry)
  const countryDisplay = aircraft.registrationCountry
    ? `${flag || ''} ${aircraft.registrationCountry}`.trim()
    : null

  // Insurance expiry with color coding
  const insuranceDays = aircraft.insuranceExpiry ? getDaysUntil(aircraft.insuranceExpiry) : null
  const insuranceVariant: 'default' | 'warning' | 'error' =
    insuranceDays !== null && insuranceDays < 0
      ? 'error'
      : insuranceDays !== null && insuranceDays <= 30
        ? 'warning'
        : 'default'

  // Annual inspection with color coding
  const inspectionDays = aircraft.annualInspectionDue
    ? getDaysUntil(aircraft.annualInspectionDue)
    : null
  const inspectionVariant: 'default' | 'warning' | 'error' =
    inspectionDays !== null && inspectionDays < 0
      ? 'error'
      : inspectionDays !== null && inspectionDays <= 30
        ? 'warning'
        : 'default'

  return (
    <div className="space-y-0">
      {/* Block 1: Aircraft Specs */}
      <CompactInfoBlock title="Aircraft" formId="aircraft-details">
        {/* Type name */}
        <div className="text-sm font-semibold text-foreground">{typeName}</div>

        {/* Registration country + year + serial */}
        <CompactRow className="mt-0.5">
          {countryDisplay && (
            <TooltipValue
              label="Registration Country"
              value={countryDisplay}
              icon={<Globe className="w-3 h-3" />}
            />
          )}
          {aircraft.yearManufactured && (
            <TooltipValue
              label="Year Manufactured"
              value={String(aircraft.yearManufactured)}
              icon={<CalendarDays className="w-3 h-3" />}
            />
          )}
          {aircraft.serialNumber && (
            <TooltipValue
              label="Serial Number"
              value={aircraft.serialNumber}
              icon={<Hash className="w-3 h-3" />}
            />
          )}
        </CompactRow>

        {/* Dimensions and MTOW */}
        <CompactRow className="mt-1">
          {dimensions && (
            <TooltipValue
              label="Length × Wingspan × Height"
              value={dimensions}
              icon={<Ruler className="w-3 h-3" />}
            />
          )}
          {weight && (
            <TooltipValue
              label="Max Takeoff Weight"
              value={weight}
              icon={<Weight className="w-3 h-3" />}
            />
          )}
        </CompactRow>

        {/* Category badge and Status */}
        <CompactRow className="mt-1">
          {categoryLabel && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
              {categoryLabel}
            </Badge>
          )}
          <Badge
            variant={AIRCRAFT_STATUS_VARIANTS[aircraft.status]}
            className="text-[10px] px-1.5 py-0 h-5"
          >
            {AIRCRAFT_STATUS_LABELS[aircraft.status]}
          </Badge>
        </CompactRow>

        {/* Location (Area • Unit) */}
        <div className="mt-1.5 pt-1.5 border-t border-border/50">
          <TooltipValue
            label="Current Location"
            value={locationDisplay}
            icon={<MapPin className="w-3 h-3" />}
            variant={allocation.areaId ? 'default' : 'muted'}
          />
        </div>
      </CompactInfoBlock>

      {/* Divider */}
      <div className="border-t border-border/50" />

      {/* Block 2: Compliance */}
      <CompactInfoBlock title="Compliance" formId="aircraft-details">
        <div className="space-y-1">
          {/* Insurance Expiry */}
          {aircraft.insuranceExpiry ? (
            <div className="flex items-center gap-2">
              <Shield className="w-3 h-3 text-muted-foreground" />
              <TooltipValue
                label="Insurance Expiry"
                value={formatDate(aircraft.insuranceExpiry)}
                variant={insuranceVariant}
              />
              {insuranceDays !== null && insuranceDays <= 30 && (
                <span className={`text-[10px] ${insuranceVariant === 'error' ? 'text-red-600' : 'text-amber-600'}`}>
                  {insuranceDays < 0
                    ? `(${Math.abs(insuranceDays)} days ago)`
                    : `(${insuranceDays} days)`}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield className="w-3 h-3" />
              <span>No insurance info</span>
            </div>
          )}

          {/* Annual Inspection */}
          {aircraft.annualInspectionDue ? (
            <div className="flex items-center gap-2">
              <Wrench className="w-3 h-3 text-muted-foreground" />
              <TooltipValue
                label="Annual Inspection Due"
                value={formatDate(aircraft.annualInspectionDue)}
                variant={inspectionVariant}
              />
              {inspectionDays !== null && inspectionDays <= 30 && (
                <span className={`text-[10px] ${inspectionVariant === 'error' ? 'text-red-600' : 'text-amber-600'}`}>
                  {inspectionDays < 0
                    ? `(${Math.abs(inspectionDays)} days ago)`
                    : `(${inspectionDays} days)`}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Wrench className="w-3 h-3" />
              <span>No inspection info</span>
            </div>
          )}

          {/* Placeholder compliance fields that need new DB columns */}
          <div className="mt-1 pt-1 border-t border-border/30">
            <div className="flex items-center justify-between text-xs text-muted-foreground/60">
              <span>Registration Expiry</span>
              <span>—</span>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground/60">
              <span>Insurance Provider</span>
              <span>—</span>
            </div>
          </div>
        </div>
      </CompactInfoBlock>

      {/* Divider */}
      <div className="border-t border-border/50" />

      {/* Block 3: Staff Notes */}
      <CompactInfoBlock title="Notes">
        {aircraft.notes ? (
          <div className="space-y-1.5">
            <div className="text-xs text-foreground bg-muted/50 rounded px-2 py-1.5 whitespace-pre-wrap leading-relaxed max-h-[80px] overflow-y-auto">
              {aircraft.notes}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[10px] text-muted-foreground"
              onClick={() => openEditForm('aircraft-notes')}
            >
              <StickyNote className="w-3 h-3 mr-1" />
              Edit Note
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-3 text-xs w-full"
            onClick={() => openEditForm('aircraft-notes')}
          >
            <StickyNote className="w-3.5 h-3.5 mr-1.5" />
            Add Note
          </Button>
        )}
      </CompactInfoBlock>
    </div>
  )
}
